/**
 * PageHeader — the page title band (design/ux/01_foundations.md §3.13, §5.4;
 * EXE-40 / PK-UIX-14 / EG-137).
 *
 *  · Row 1: breadcrumb — real links (`to`) in a « Fil d'Ariane » nav, the
 *    current page `aria-current="page"`. Inside the pharmacy shell it is
 *    derived from NAV_TREE (WP-18, ux/01 §6.3): category (opens its flyout)
 *    › sub-category (its first page) › page › document label.
 *  · Row 2: the h1 (24 px / 700) with its status badge, and the actions. The
 *    h1 is never squeezed: it keeps at least min(100 %, 18 rem) and its words
 *    wrap; the actions wrap onto the next line instead of overlapping it —
 *    down to the 390 px drawer layout.
 *  · Structured `actions` (array): at most 3 inline on desktop, 2 on a
 *    tablet, the primary only on a phone; the rest in « Plus… », a real menu
 *    button. A ReactNode `actions` is laid out as given, wrapping.
 *  · `tabs`: the Tabs strip scrolls inside itself, never the page.
 *  · `variant="compact"` (document and entry screens with an ActionBar):
 *    56 px — breadcrumb line, h1 + status badge + document identity; the
 *    document verbs live in the ActionBar.
 */
import * as React from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button, Icon, Tabs, type ButtonProps, type TabItem } from "./primitives";
import { MenuButton } from "./Menu";
import { inlineActionBudget, splitActions } from "@/lib/page-header.js";
import { breadcrumbFor, documentLabelFor } from "@/lib/nav-tree.js";
import { ShellNavContext } from "@/components/layout/nav/ShellNavContext";

export interface Crumb {
  label: React.ReactNode;
  to?: string;
  onClick?: () => void;
}

export interface PageHeaderAction {
  id: string;
  label: string;
  icon?: string;
  onClick?: () => unknown;
  variant?: ButtonProps["variant"];
  disabled?: boolean;
  disabledReason?: React.ReactNode;
  shortcut?: string;
  loading?: boolean;
  hidden?: boolean;
  /** Router target: renders a link-button. */
  to?: string;
}

export interface PageHeaderProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Status badge next to the h1. */
  status?: React.ReactNode;
  /** Compact variant: document identity after the h1. */
  identity?: React.ReactNode;
  /**
   * The page's own crumbs — shown only outside a menu location (inside the
   * pharmacy shell the breadcrumb comes from NAV_TREE, ux/01 §6.3).
   */
  breadcrumbs?: Crumb[];
  /**
   * A detail page's document (« FA-2026-0142 »): the last crumb and the start
   * of the window title. Without it, a page reached through a leaf's `match`
   * pattern contributes the last crumb it names.
   */
  documentLabel?: string | number | null;
  actions?: React.ReactNode | PageHeaderAction[];
  tabs?: TabItem[];
  tabValue?: string;
  onTabChange?: (v: string) => void;
  tabsLabel?: string;
  tabsIdBase?: string;
  variant?: "normal" | "compact";
  className?: string;
}

function useViewportWidth() {
  const get = () => (typeof window !== "undefined" ? window.innerWidth : null);
  const [w, setW] = React.useState<number | null>(get);
  React.useEffect(() => {
    const on = () => setW(window.innerWidth);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return w;
}

export const Breadcrumb: React.FC<{ items: Crumb[] }> = ({ items }) => (
  <nav aria-label="Fil d'Ariane" className="pk-breadcrumb">
    <ol>
      {items.map((b, i) => {
        const last = i === items.length - 1;
        return (
          <li key={i}>
            {i > 0 && <Icon name="chevron-right" size={14} aria-hidden="true" />}
            {last ? (
              <span aria-current="page">{b.label}</span>
            ) : b.to ? (
              <Link to={b.to}>{b.label}</Link>
            ) : b.onClick ? (
              <button type="button" onClick={b.onClick}>
                {b.label}
              </button>
            ) : (
              <span>{b.label}</span>
            )}
          </li>
        );
      })}
    </ol>
  </nav>
);

function ActionButton({ a }: { a: PageHeaderAction }) {
  const common = {
    variant: a.variant ?? "secondary",
    size: "md" as const,
    icon: a.icon,
    disabled: a.disabled,
    disabledReason: a.disabledReason,
    shortcut: a.shortcut,
    loading: a.loading,
  };
  return a.to ? (
    <Button {...common} as={Link} to={a.to}>
      {a.label}
    </Button>
  ) : (
    <Button {...common} onClick={() => a.onClick?.()}>
      {a.label}
    </Button>
  );
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  status,
  identity,
  breadcrumbs = [],
  documentLabel,
  actions,
  tabs,
  tabValue,
  onTabChange,
  tabsLabel,
  tabsIdBase,
  variant = "normal",
  className,
}) => {
  const vw = useViewportWidth();
  const structured = Array.isArray(actions);
  const { inline, overflow } = structured
    ? splitActions(actions as PageHeaderAction[], inlineActionBudget(vw))
    : { inline: [], overflow: [] };
  const hasActions = structured ? inline.length + overflow.length > 0 : !!actions;

  // Inside the shell, a menu location replaces the hand-written crumbs.
  const shell = React.useContext(ShellNavContext);
  const active = shell?.active ?? null;
  const doc = active ? documentLabelFor(active, documentLabel, breadcrumbs) : null;
  const pathname = shell?.pathname;
  const setDocumentLabel = shell?.setDocumentLabel;
  React.useEffect(() => {
    if (pathname != null) setDocumentLabel?.(pathname, doc);
  }, [pathname, doc, setDocumentLabel]);
  const crumbs: Crumb[] = active
    ? breadcrumbFor(active, doc, { flat: shell?.mode === "flat" }).map((c: any) => ({
        label: c.label,
        to: c.to,
        onClick: c.categoryId ? () => shell?.requestFlyout(c.categoryId) : undefined,
      }))
    : breadcrumbs;

  return (
    <header className={cn("pk-page-header", variant === "compact" && "pk-page-header--compact", className)}>
      {crumbs.length > 0 && <Breadcrumb items={crumbs} />}
      <div className="pk-page-head-row">
        <div className="pk-page-title-block">
          <div className="pk-page-title-line">
            <h1 className="pk-page-title">{title}</h1>
            {status}
            {identity && <span className="pk-page-identity">{identity}</span>}
          </div>
          {subtitle && <p className="pk-page-subtitle">{subtitle}</p>}
        </div>
        {hasActions && (
          <div className="pk-page-actions">
            {structured ? (
              <>
                {inline.map((a) => (
                  <ActionButton key={a.id} a={a} />
                ))}
                {overflow.length > 0 && (
                  <MenuButton
                    label="Plus…"
                    size="md"
                    align="end"
                    items={overflow.map((a) => ({
                      id: a.id,
                      label: a.label,
                      icon: a.icon,
                      disabled: a.disabled || !!a.disabledReason,
                      shortcut: a.shortcut,
                      danger: a.variant === "danger",
                      onSelect: () => a.onClick?.(),
                    }))}
                  />
                )}
              </>
            ) : (
              actions
            )}
          </div>
        )}
      </div>
      {tabs && tabs.length > 0 && (
        <div className="pk-page-tabs">
          <Tabs
            items={tabs}
            value={tabValue ?? ""}
            onChange={onTabChange}
            label={tabsLabel ?? (typeof title === "string" ? title : undefined)}
            idBase={tabsIdBase}
          />
        </div>
      )}
    </header>
  );
};
